// Posting — patient-credit seed rows and the state rules (H1 §3, §5; H8). Pure data
// and helpers, no imports from the Posting store (so history and store can both use it).
import { addDays, longDate, shortDate } from './dates';
import type { PaymentRef } from './types';

export type CreditState = 'still' | 'refunded' | 'used';

export interface CreditSeed {
  id: string;
  patient: string;
  payer: string;
  /** What the insurance payment left on the account. */
  amount: number;
  /** The day it was posted in Dentrix (the credit rules count from here). */
  postedIso: string;
  payment: PaymentRef;
  state: CreditState;
  /** The day it was refunded or used. */
  stateIso?: string;
  pronoun: 'he' | 'she';
  /** Only exists in this scenario (`?scenario=california`). */
  onlyIn?: 'california';
}

/** The credits ELVA reports in Posted. Total is always computed from these. */
export const CREDIT_SEEDS: CreditSeed[] = [
  {
    id: 'PH-CR-HUGO', patient: 'Hugo Brandt', payer: 'Guardian', amount: 612, postedIso: '2026-10-15',
    payment: { method: 'check', checkNo: '77134406' }, state: 'still', pronoun: 'he',
  },
  {
    id: 'PH-CR-ROSA', patient: 'Rosa Vidal', payer: 'MetLife', amount: 410, postedIso: '2026-10-09',
    payment: { method: 'eft', trace: '1T5520918' }, state: 'refunded', stateIso: '2026-10-14', pronoun: 'she',
  },
  {
    id: 'PH-CR-DEAN', patient: 'Dean Ortiz', payer: 'Sun Life', amount: 262, postedIso: '2026-10-06',
    payment: { method: 'card', last4: '5521', approval: '038812' }, state: 'used', stateIso: '2026-10-20', pronoun: 'he',
  },
  {
    id: 'PH-CR-NORA', patient: 'Nora Quinn', payer: 'Sun Life', amount: 188, postedIso: '2026-10-04',
    payment: { method: 'eft', trace: '1T5520774' }, state: 'still', pronoun: 'she', onlyIn: 'california',
  },
];

export function isCaliforniaScenario(): boolean {
  try {
    return new URLSearchParams(window.location.search).get('scenario') === 'california';
  } catch {
    return false;
  }
}

/** The date the credit states are read at: today is Oct 21; in the California scenario it is Nov 16 (H8). */
export function creditAsOfIso(): string {
  return isCaliforniaScenario() ? '2026-11-16' : '2026-10-21';
}

export function creditSeeds(): CreditSeed[] {
  const ca = isCaliforniaScenario();
  return CREDIT_SEEDS.filter((c) => !c.onlyIn || ca);
}

/** Days from `iso` to `asOf`. */
export function daysBetween(iso: string, asOf: string): number {
  return Math.round((Date.parse(`${asOf}T12:00:00Z`) - Date.parse(`${iso}T12:00:00Z`)) / 86_400_000);
}

const OBJ = { he: 'him', she: 'her' } as const;
const POSS = { he: 'his', she: 'her' } as const;
export const objectPronoun = (p: 'he' | 'she'): string => OBJ[p];
export const possessivePronoun = (p: 'he' | 'she'): string => POSS[p];

/** California: remind after 30 days, tell the patient within 90. Elsewhere: remind after 60. */
export function creditDates(sinceIso: string, california: boolean): { remindIso: string; tellByIso?: string } {
  return california
    ? { remindIso: addDays(sinceIso, 30), tellByIso: addDays(sinceIso, 90) }
    : { remindIso: addDays(sinceIso, 60) };
}

/** The notice under Hugo's breakdown (H1 §3). */
export function creditNotice(firstName: string, pronoun: 'he' | 'she', sinceIso: string, california: boolean): string {
  const { remindIso, tellByIso } = creditDates(sinceIso, california);
  const first = `Refunding the credit or keeping it for ${firstName}’s next visit is the practice’s call. ELVA doesn’t move it.`;
  if (california && tellByIso) {
    return `${first} California: tell ${firstName} within 90 days (by ${longDate(tellByIso)}), then refund within 30 days unless ${pronoun} asks to keep it. If it’s still on ${possessiveOf(pronoun)} account on ${shortDate(remindIso)}, ELVA asks you in Human in the loop.`;
  }
  return `${first} Texas: no refund deadline found; a credit unclaimed for 3 years must be reported to the state as unclaimed property. If it’s still on ${possessiveOf(pronoun)} account on ${shortDate(remindIso)}, ELVA asks you in Human in the loop.`;
}
function possessiveOf(p: 'he' | 'she'): string {
  return POSS[p];
}
